import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TreeItem from '@mui/lab/TreeItem';
import TreeView from '@mui/lab/TreeView';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import React from 'react';
import SidePanelLinkContainer from './SidePanelLinkContainer';

export interface TreeItemInterface {
	id: string;
	children: string[];
	hasChildren?: boolean;
	isExpanded?: boolean;
	isChildrenLoading?: boolean;
	data?: any;
}

export interface TreeItemData {
	[x: string]: TreeItemInterface;
}

export interface TreeData {
	rootId: string;
	items: TreeItemData;
}

interface Props {
	treeData: TreeData;
	defaultExpanded: string[];
}

const useStyles = makeStyles({
	root: {
		flexGrow: 1,
		maxWidth: 400,
		paddingLeft: '3px',
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	treeItem: {
		backgroundColor: '#FFFFFF',
	},
});

export default function TreeNode({ treeData, defaultExpanded }: Props) {
	const classes = useStyles();
	const [expanded, setExpanded] = React.useState<string[]>(defaultExpanded);

	React.useEffect(() => {
		setExpanded((prevExpanded) =>
			_.uniq([...prevExpanded, ...defaultExpanded]),
		);
	}, [defaultExpanded]);

	const handleToggle = (event: React.ChangeEvent<{}>, nodeIds: string[]) => {
		setExpanded(nodeIds);
	};

	const rootChildren = treeData.items[treeData.rootId].children;

	const renderTreeItem = (node: TreeItemInterface) => {
		const { data, children } = node;

		const renderTreeChildren = () => {
			return children.map((nextChild: string) => {
				const nextNode = treeData.items[nextChild];
				return nextNode ? renderTreeItem(nextNode) : null;
			});
		};

		const TreeLink = (
			<SidePanelLinkContainer onLabelClick={() => data.link} item={node} />
		);

		return (
			<TreeItem
				className={classes.treeItem}
				key={node.id}
				nodeId={node.id}
				label={TreeLink}
			>
				{Array.isArray(children) ? renderTreeChildren() : null}
			</TreeItem>
		);
	};

	const renderedTree = rootChildren.map((child: string) => {
		const node = treeData.items[child];

		return renderTreeItem(node);
	});
	const selectedKey = Object.keys(treeData.items).find(
		(key) => !!treeData.items[key].data.selected,
	);
	const selectedItem = treeData.items[selectedKey];

	return (
		<TreeView
			className={classes.root}
			defaultCollapseIcon={<ExpandMoreIcon />}
			defaultExpandIcon={<ChevronRightIcon />}
			selected={selectedItem ? ([selectedItem.id] as string[]) : []}
			expanded={expanded}
			onNodeToggle={handleToggle}
		>
			{renderedTree}
		</TreeView>
	);
}
